import { Inject } from '@odinjs/odin';
import { PanelAPI } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, PseudonymAppointmentsConfirmationAliases } from '../../../codegen';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import { APPOINTMENT_TYPE } from '../../../constants/appointment-type';
import { Data } from '../../../interfaces/data';
import UserContext from '../../../utils/user-context';
import PseudonymAppointmentsBaseController from '../pseudonym-appointments-base-controller';
import { AppointmentConfirmationData, ConfirmedSchedule } from '../pseudonym-interfaces';
import { ScheduleAvailableType } from '../pseudonym-types';
import PseudonymUtils from '../pseudonym-utils';

@Controller({ component: PseudonymAppointmentsConfirmationAliases.LAYOUT_CONFIRMATION, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_CONFIRMATION })
export default class PseudonymAppointmentsConfirmationController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsConfirmationAliases.PANEL_CONTEXTUALBAR_REQUEST)
    panelContextualBarRequestsApi!: PanelAPI;

  @Inject(PseudonymAppointmentsConfirmationAliases.PANEL_CONTEXTUALBAR_WAITINGLIST)
    panelContextualBarWaitinglistApi!: PanelAPI;

  @Inject(PseudonymAppointmentsConfirmationAliases.PANEL_CONTEXTUALBAR_APPOINTMENTS)
    panelContextualBarAppointmentsApi!: PanelAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnMount({ target: PseudonymAppointmentsConfirmationAliases.LAYOUT_CONFIRMATION })
  handleOnMountLayoutConfirmation(): void {
    this.panelContextualBarRequestsApi.setVisible(false);
    this.panelContextualBarWaitinglistApi.setVisible(false);
    this.panelContextualBarAppointmentsApi.setVisible(false);
  }

  @OnMount({ target: PseudonymAppointmentsConfirmationAliases.LAYOUT_CONTENT })
  handleOnMountLayoutContent(event: MountEvent): void {
    if (!event) {
      return;
    }

    const scheduleInfoList = this.context.get('data') as Array<ConfirmedSchedule>;
    const scheduleList: Array<Data> = [];
    const scheduleRequestList: Array<Data> = [];
    const scheduleWaitingList: Array<Data> = [];
    const appointmentDetailspromises: Array<Promise<void>> = [];

    scheduleInfoList.forEach(scheduleInfo => {
      if (!scheduleInfo.nrSequencia) {
        return;
      }

      const appointmentParameters = this.mountAppointmentDetailParameters(scheduleInfo);
      const promise = HttpService.get(APPLICATION_ENDPOINTS.APPOINTMENT_DETAIL, appointmentParameters)
        .then(response => {
          const appointment = response.body as Record<string, Value>;
          switch (appointmentParameters?.ieScheduleType) {
            case ScheduleAvailableType.REQUEST:
              scheduleRequestList.push(appointment as Data);
              break;
            case ScheduleAvailableType.WAITING_LIST:
              scheduleWaitingList.push(appointment as Data);
              break;
            default:
              scheduleList.push(appointment as Data);
              break;
          }
        });
      appointmentDetailspromises.push(promise);
    });

    Promise.all(appointmentDetailspromises)
      .then(() => {
        let appointmentConfirmationData = {} as AppointmentConfirmationData;

        if (scheduleList.length > 0) {
          this.panelContextualBarAppointmentsApi.setVisible(true);
          appointmentConfirmationData = { data: scheduleList, isConfirmationResult: true };
        }
        else if (scheduleRequestList.length > 0) {
          this.panelContextualBarRequestsApi.setVisible(true);
          appointmentConfirmationData = { data: scheduleRequestList, isConfirmationResult: true };
        }
        else if (scheduleWaitingList.length > 0) {
          this.panelContextualBarWaitinglistApi.setVisible(true);
          appointmentConfirmationData = { data: scheduleWaitingList, isConfirmationResult: true };
        }

        this.context.set({ appointment_confirmation_data: appointmentConfirmationData });
      });
  }

  private mountAppointmentDetailParameters(confirmedSchedule: ConfirmedSchedule): Record<string, Value> {
    const appointmentType = PseudonymUtils.parseScheduleTypeToAppointmentType(confirmedSchedule.ieTipoAgendamento);
    const appointmentParameters = {
      appointmentType: confirmedSchedule.ieStatus === ScheduleAvailableType.WAITING_LIST ? APPOINTMENT_TYPE.WAITING_LIST : appointmentType,
      ieScheduleType: confirmedSchedule.ieStatus,
      nrSeq: confirmedSchedule.nrSequencia,
      patientId: UserContext.patientId,
    }

    return appointmentParameters;
  }

}
